import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { linkIDs } from '../../../../services/link-service/links';
import { HelpArticleLink } from '../../../../components/help-article-link';

import { LabeledRow } from '../../../../fulfillment-planner/labeled-row';
import { DutiesAndTaxesDropdown } from '../../../../fulfillment-planner/duties-and-taxes/dropdown';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { BulkActions } from '../../../../enums/bulk-actions';
import { PrepaidDuty, SenderPrepay } from '../../../../fulfillment-planner/duties-and-taxes/constants';
import { dutiesAndTaxesMessages } from '../../../../fulfillment-planner/duties-and-taxes/messages';
import Global from '../../../../store/global-selectors';

import { messages } from './messages';
import style from './style.module.css';

export const validateDutiesAndTaxes = (jobData = emptyObject) => {
  const { availableAdditionalOptions = emptyArray } = jobData;
  const isValid = availableAdditionalOptions.length !== 0;
  return isValid;
};

const DutiesAndTaxesComponent = ({ onChange, model }) => {
  const lastAddedJobId = useSelector(Global.selectBulkUpdateLastAddedJobId);
  const { jobData } =
    useSelector((state) =>
      Global.selectBulkUpdateBulkUpdateJob(state, {
        bulkUpdateJobId: lastAddedJobId,
      }),
    ) || emptyObject;

  const [isValid, setIsValid] = useState(true);
  const [prepaidDutyValue, setPrepaidDutyValue] = useState(SenderPrepay);

  const { shippingConfiguration = {} } = model;
  const { options } = shippingConfiguration;

  const updateShippingConfiguration = useCallback(
    (prepaidDuty) => {
      onChange({
        ...model,
        shippingConfiguration: {
          ...shippingConfiguration,
          options: {
            ...options,
            additionalOptions: [
              {
                additionalOptionEnum: PrepaidDuty,
                additionalOptionValue: { prepaidDuty, price: null },
              },
            ],
          },
        },
      });
    },
    [onChange, options, model, shippingConfiguration],
  );

  const handleOnChange = useCallback(
    (selectedOption) => {
      updateShippingConfiguration(selectedOption);
      setPrepaidDutyValue(selectedOption);
    },
    [updateShippingConfiguration],
  );

  useEffect(() => {
    setIsValid(validateDutiesAndTaxes(jobData));
    updateShippingConfiguration(SenderPrepay);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <p className={style['info-message']}>
        <FormattedMessage
          {...messages.infoMessage}
          values={{
            a: (chunks) => <HelpArticleLink articleId={linkIDs.InternationalDutiesBilling}>{chunks}</HelpArticleLink>,
          }}
        />
      </p>
      <LabeledRow className={classNames({ [style.error]: !isValid })} label={<FormattedMessage {...dutiesAndTaxesMessages.label} />}>
        <DutiesAndTaxesDropdown
          {...(!isValid && {
            inlineMessage: <FormattedMessage {...messages.errorMessage} />,
            inlineMessageType: INLINE_MESSAGE_TYPES.error,
          })}
          onChange={handleOnChange}
          selectedOptionValue={prepaidDutyValue}
          shouldHidePrice
        />
      </LabeledRow>
    </>
  );
};

DutiesAndTaxesComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
  model: PropTypes.object.isRequired,
};

export const DutiesAndTaxes = {
  id: BulkActions.dutiesAndTaxes.name,
  render: (onChange, model) => <DutiesAndTaxesComponent model={model} onChange={onChange} />,
};
